// collector/previewStore.js
//
// The saved copies of shops' supplier previews (supplier-preview-offline P2-A,
// locked answer L-G): ONE IndexedDB database `poolit-preview` v1, one store —
//
//   shops   key `shopId`   { shopId, name, source: 'day'|'picked', pickedAt?,
//                            recordedAt, responses: { requestKey: rawBody },
//                            photoUrls: [], lead: null|{ id, phone, at, status, attempts } }
//
// `responses` holds each recorded read's body exactly as the server sent it
// (collector/previewReplay.js records and replays them). A database of its own
// on purpose, NEVER a store inside `poolit-collector`: a version bump there
// closes an open old tab's connection, and that tab's code then falls back to
// memory with its unsent taps in it.
//
// Built on collector/db.js's raw-wrapper rules (`openDatabase`, strict
// durability, the memory twin from the first refused open on). Cleared on an
// explicit logout; never on a 401.
import { openDatabase, readAll, STRICT, withMemoryFallback } from '@/collector/db.js';
import { COPY_MAX_AGE_MS } from '@/collector/previewReplay.js';

export const PREVIEW_DB_NAME = 'poolit-preview';
export const PREVIEW_DB_VERSION = 1;
const SHOPS = 'shops';

export { COPY_MAX_AGE_MS };

/** Ids are strings on the wire (JSON:API) — rows are keyed by the string. */
export const shopKey = (shopId) => String(shopId);

/**
 * A copy the preview may open: recorded less than 24 hours ago, with its
 * reads. The profile must name the row's own shop (design Section 2.G.3) —
 * checked where the profile is read (useSupplierPreview).
 */
export function isUsableCopy(row, now = Date.now()) {
  const recordedAt = Date.parse(row?.recordedAt ?? '');
  if (!Number.isFinite(recordedAt) || now - recordedAt >= COPY_MAX_AGE_MS) return false;
  return row?.responses != null && Object.keys(row.responses).length > 0;
}

function upgrade(db) {
  if (!db.objectStoreNames.contains(SHOPS)) db.createObjectStore(SHOPS, { keyPath: 'shopId' });
}

function createIndexedDbPreviewStore(factory) {
  const { run } = openDatabase(factory, {
    name: PREVIEW_DB_NAME,
    version: PREVIEW_DB_VERSION,
    upgrade,
    label: 'collector/previewStore',
  });

  return {
    get: (shopId) =>
      run(SHOPS, 'readonly', (tx, out) => {
        const request = tx.objectStore(SHOPS).get(shopKey(shopId));
        request.onsuccess = () => {
          out.value = request.result ?? null;
        };
      }),

    put: (row) =>
      run(
        SHOPS,
        'readwrite',
        (tx) => {
          tx.objectStore(SHOPS).put({ ...row, shopId: shopKey(row.shopId) });
        },
        STRICT,
      ),

    list: async () => {
      const all = await run(SHOPS, 'readonly', (tx, out) => readAll(tx, SHOPS, out, 'shops'));
      return all?.shops ?? [];
    },

    remove: (shopId) =>
      run(
        SHOPS,
        'readwrite',
        (tx) => {
          tx.objectStore(SHOPS).delete(shopKey(shopId));
        },
        STRICT,
      ),

    clear: () =>
      run(
        SHOPS,
        'readwrite',
        (tx) => {
          tx.objectStore(SHOPS).clear();
        },
        STRICT,
      ),
  };
}

/** The in-memory twin: same methods, structured clone on the way in and out. */
export function createMemoryPreviewStore() {
  const rows = new Map();
  const clone = (value) => structuredClone(value);
  // Settles on a later task, like an IndexedDB transaction does.
  const settle = (fn) => Promise.resolve().then(fn);

  return {
    kind: 'memory',
    rows,
    get: (shopId) => settle(() => (rows.has(shopKey(shopId)) ? clone(rows.get(shopKey(shopId))) : null)),
    put: (row) =>
      settle(() => {
        const copy = clone({ ...row, shopId: shopKey(row.shopId) });
        rows.set(copy.shopId, copy);
      }),
    list: () => settle(() => [...rows.values()].map(clone)),
    remove: (shopId) =>
      settle(() => {
        rows.delete(shopKey(shopId));
      }),
    clear: () =>
      settle(() => {
        rows.clear();
      }),
  };
}

let active = null;

/** The saved copies (created on first use). */
export function previewStore() {
  if (!active) {
    const factory = typeof indexedDB === 'undefined' ? null : indexedDB;
    active = factory
      ? withMemoryFallback(
        createIndexedDbPreviewStore(factory),
        createMemoryPreviewStore(),
        ['get', 'put', 'list', 'remove', 'clear'],
        'collector/previewStore',
      )
      : createMemoryPreviewStore();
  }
  return active;
}

/** Tests: use this store (a fresh twin when none is given). */
export function setPreviewStoreForTests(store = createMemoryPreviewStore()) {
  active = store;
  return store;
}
